import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { routes as mainRoutes } from '../../../routes';
import { Card } from 'react-bootstrap';
import { WorkflowItemsFilterForMeConnected } from '../Filters/WorkflowItemsFilterForMeConnected';
import ServerError from '../../../../../components/ServerError';
import { Toggle } from '../Common/Toggle';
import { WorkflowItemSidebar } from '../WorkflowItemSidebar/WorkflowItemSidebar/WorkflowItemSidebar';
import { CardHeaderWithFilter } from '../Common/CardHeaderWithFilter';
import { Maybe } from '../../../../../utils/maybe';
import { Item } from './WorkflowItemsTable';
import { useFlairTableWithPaginationAndFilter } from '../../../../../hooks/useFlairTableWithPaginationAndFilter';
import {
  WorkflowContext,
  useWorkflowsItemsQuery,
} from '../../../__generated__/graphql';
import {
  filterActiveStepItems,
  mapItemsSortBy,
  mapWorkflowItems,
  mapWorkflowItemsFilterInput2,
} from './mappings';
import { WorkflowItemsTableConnected } from './WorkflowItemsTableConnected';
import { emptyWorkflowItemFilterForMe } from '../Filters/types';
import { SkeletonTable } from '../../../components/Skeleton/Table';
import { useUserInfo } from '../../../context/UserInfo';

const PAGE_SIZE = 50;
const MAX_CLIENT_FILTER_LIMIT = 10000;

export const WorkflowItemsForMe: React.FC = () => {
  const [selectedItem, setSelectedItem] = useState<Maybe<Item>>(null);

  const { id: meId } = useUserInfo();

  const {
    tableState,
    handleFilterChange,
    handlePageIndexChanged,
    handleSortByChanged,
    setTotalItemsCount,
  } = useFlairTableWithPaginationAndFilter({
    defaultFilter: emptyWorkflowItemFilterForMe,
    storageKey: 'workflow_items_filter_for_me',
    createRouteUrl: (queryParams) =>
      mainRoutes.workflowsMeWorkflowItemsAll.route
        .withQueryParams(queryParams)
        .create({}),
  });

  const isFilteringInactiveSteps = tableState.filter.status === 'NOT_STARTED';

  const { data, error, loading } = useWorkflowsItemsQuery({
    variables: {
      workflowContext: WorkflowContext.My,
      filter: mapWorkflowItemsFilterInput2(tableState.filter),
      sort: mapItemsSortBy(tableState.sortBy) || null,
      pagination: isFilteringInactiveSteps
        ? { offset: 0, limit: MAX_CLIENT_FILTER_LIMIT }
        : {
            offset: tableState.pageIndex * PAGE_SIZE,
            limit: PAGE_SIZE,
          },
      skipPaginationInfo:
        isFilteringInactiveSteps || tableState.totalItemsCount !== undefined,
    },
  });

  const filteredWorkflowItems = useMemo(() => {
    if (!data) {
      return undefined;
    }
    const items = mapWorkflowItems(data);
    return isFilteringInactiveSteps ? filterActiveStepItems(items) : items;
  }, [data, isFilteringInactiveSteps]);

  const paginatedWorkflowItems = useMemo(() => {
    if (!filteredWorkflowItems) {
      return undefined;
    }
    if (!isFilteringInactiveSteps) {
      return filteredWorkflowItems;
    }
    const start = tableState.pageIndex * PAGE_SIZE;
    return filteredWorkflowItems.slice(start, start + PAGE_SIZE);
  }, [filteredWorkflowItems, isFilteringInactiveSteps, tableState.pageIndex]);

  const itemsCount = isFilteringInactiveSteps
    ? filteredWorkflowItems?.length
    : tableState.totalItemsCount;

  useEffect(() => {
    if (!isFilteringInactiveSteps) {
      const totalCount = data?.workflowItems.paginationInfo?.totalCount;
      setTotalItemsCount(totalCount);
    }
  }, [setTotalItemsCount, data, isFilteringInactiveSteps]);

  const renderContent = useCallback(() => {
    if (loading || !paginatedWorkflowItems || !meId) {
      return <Loading />;
    }
    return (
      <WorkflowItemsTableConnected
        workflowItems={paginatedWorkflowItems}
        employeeColumnVisible={false}
        onItemClick={setSelectedItem}
        pageSize={PAGE_SIZE}
        itemsCount={itemsCount}
        initialSortBy={tableState.sortBy}
        onSortByChanged={handleSortByChanged}
        initialPageIndex={tableState.pageIndex}
        onPageIndexChanged={handlePageIndexChanged}
      />
    );
  }, [
    loading,
    paginatedWorkflowItems,
    meId,
    itemsCount,
    handleSortByChanged,
    handlePageIndexChanged,
    tableState,
  ]);

  if (error) {
    return <ServerError />;
  }

  const item =
    filteredWorkflowItems?.find((x) => x.id === selectedItem?.id) ?? null;

  return (
    <>
      <Card>
        <CardHeaderWithFilter>
          <WorkflowItemsFilterForMeConnected
            filter={tableState.filter}
            onChange={handleFilterChange}
          />
          <Toggle mode="forMe" value="workflowItems" />
        </CardHeaderWithFilter>
        {renderContent()}
      </Card>
      <WorkflowItemSidebar
        workflowId={item?.workflowId ?? null}
        workflowItem={item}
        showComments={selectedItem?.showComments}
        onClose={() => setSelectedItem(null)}
      />
    </>
  );
};

const Loading: React.FC = () => {
  return <SkeletonTable columns={6} rows={5} />;
};
